import { createAsyncThunk } from "@reduxjs/toolkit";
import { getCasesAPI, addCaseDetails, 
  updateCaseDetails, updateCaseJsonAPI, removeCases, 
  getCaseTypesAPI,getTableColumnNames } from "../../helpers/fakebackend_helper";
import { toast } from "react-toastify";
import { t } from "i18next";

import * as url from "../../helpers/url_helper";
import { updateCaseProcessingStatus } from "./reducer";
import { getAuthUser } from "../../helpers/utils";
const { user } = getAuthUser();

export const setCaseUploadProgress = (percent) => ({
  type: "cases/setCaseUploadProgress",
  payload: percent,
});

export const uploadCaseFile = createAsyncThunk(
  "cases/uploadCaseFile",
  // 1. Accept 'isMultiUpload' in the payload. Default it to false for backward compatibility.
  async ({ formData, onProgress, isMultiUpload = false }, { rejectWithValue, dispatch }) => {
    try {
      const response = await fetch(url.UPLOAD_CASE, {
        method: "POST",
        body: formData,
      });

      if (!response.body) {
        throw new Error("No response body for stream");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      dispatch(setCaseUploadProgress(0));

      const progressMap = {
        init: 0,
        file_uploaded: 20,
        file_ocr_done: 40,
        other_data: 60,
        file_processed: 80,
        all_done: 100,
      };

      return new Promise(async (resolve, reject) => {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            resolve(); // Stream ended
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split("\n\n");
          buffer = events.pop();

          for (const eventBlock of events) {
            if (!eventBlock) continue;
            
            const lines = eventBlock.split("\n");
            let eventName = "";
            let eventData = "";

            for (const line of lines) {
              if (line.startsWith("event:")) eventName = line.slice(6).trim();
              else if (line.startsWith("data:")) eventData += line.slice(5).trim();
            }

            try {
              if (!eventName || !eventData) continue;
              const parsedData = JSON.parse(eventData);

              // 2. This is the core logic change
              if (eventName === "duplicate") {
                // ALWAYS forward the event to the UI for immediate feedback
                onProgress({ event: eventName, data: parsedData });

                if (isMultiUpload) {
                  // For multi-upload, this is NOT a fatal error.
                  // We just continue listening for the next file's events.
                  continue; 
                } else {
                  // For single-upload, this IS a fatal error.
                  // Reject the promise to trigger the component's global error state.
                  reject(new Error(parsedData.message || 'duplicateFile'));
                  return; // Stop the entire process
                }
              }
              
              const percent = progressMap[eventName] ?? 0;
              if (percent > 0) dispatch(setCaseUploadProgress(percent));
              
              onProgress({ event: eventName, data: parsedData });

              if (eventName === "error") {
                dispatch(updateCaseProcessingStatus({ ID: parsedData.DBID, Status: 11, JsonURL: null, ModifiedByID: user.ID}))
                toast.error(parsedData.message || t("Upload failed"));
                reject(new Error(parsedData.message || "Upload failed"));
                return;
              }

              if (eventName === "file_processed") {
                const data = typeof parsedData === "string" ? JSON.parse(parsedData) : parsedData;
                const payload = {
                  ID: data.DBID,
                  JsonURL: parsedData.url,
                  Status: 2,
                  ModifiedByID: user.ID
                };
                dispatch(updateCaseProcessingStatus(payload));
              }

              if (eventName === "all_done") {
                resolve(parsedData);
              }
            } catch (e) {
              console.warn("Invalid SSE event data:", eventData, e);
            }
          }
        }
      });
    } catch (err) {
      console.error("Upload Stream Failed:", err);
      return rejectWithValue(err.message || "errorUploadingDocument");
    }
  }
);

export const getCaseJson = createAsyncThunk(
  "cases/getCaseJson",
  // 1. Accept 'isMultiUpload' in the payload. Default it to false for backward compatibility.
  async ({ formData, onProgress, isMultiUpload = false }, { rejectWithValue, dispatch }) => {
    try {
      const response = await fetch(url.JSON_CASE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: formData,
      });

      if (!response.body) {
        throw new Error("No response body for stream");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      dispatch(setCaseUploadProgress(0));

      const progressMap = {
        init: 0,
        creating: 25,
        new_json: 50,
        other_data: 75,
        file_processed: 100,
      };

      return new Promise(async (resolve, reject) => {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            resolve(); // Stream ended
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split("\n\n");
          buffer = events.pop();

          for (const eventBlock of events) {
            if (!eventBlock) continue;
            
            const lines = eventBlock.split("\n");
            let eventName = "";
            let eventData = "";

            for (const line of lines) {
              if (line.startsWith("event:")) eventName = line.slice(6).trim();
              else if (line.startsWith("data:")) eventData += line.slice(5).trim();
            }

            try {
              if (!eventName || !eventData) continue;
              const parsedData = JSON.parse(eventData);
              
              const percent = progressMap[eventName] ?? 0;
              if (percent > 0) dispatch(setCaseUploadProgress(percent));
              
              onProgress({ event: eventName, data: parsedData });

              if (eventName === "error") {
                dispatch(updateCaseProcessingStatus({ ID: parsedData.DBID, Status: 11, JsonURL: null, ModifiedByID: user.ID}))
                toast.error(parsedData.message || t("Upload failed"));
                reject(new Error(parsedData.message || "Upload failed"));
                return;
              }

              if (eventName === "file_processed") {
                const data = typeof parsedData === "string" ? JSON.parse(parsedData) : parsedData;
                const payload = {
                  ID: data.DBID,
                  Status: 5,
                  JsonURL: parsedData.url,
                  ModifiedByID: user.ID
                };
                dispatch(updateCaseProcessingStatus(payload));
              }

              if (eventName === "all_done") {
                resolve(parsedData);
              }
            } catch (e) {
              console.warn("Invalid SSE event data:", eventData, e);
            }
          }
        }
      });
    } catch (err) {
      console.error("Upload Stream Failed:", err);
      return rejectWithValue(err.message || "errorUploadingDocument");
    }
  }
);

// Fetch Cases Table Column Names
export const fetchCaseTableColumnNames = createAsyncThunk(
  "cases/fetchTableColumnNames",
  async (cases, thunkAPI) => {
    try {
      const response = await getTableColumnNames(cases); 
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error || "Something went wrong");
    }
  }
);

// Thunk to fetch all cases
export const fetchAllCases = createAsyncThunk(
  "cases/fetchAll",
  async (data, thunkAPI) => {
    try {
      const response = await getCasesAPI(data);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error || "Something went wrong");
    }
  }
);

// Thunk to save cases Details
export const addCase = createAsyncThunk(
  "cases/add",
  async (cases, { rejectWithValue }) => {
    try {
      const response = await addCaseDetails(cases);
      toast.success(t("DocumentDetailsSavedMessage"));
      return response.data;
    } catch (error) {
      return rejectWithValue(error || error.message || t("Error saving document Details"));
    }
  }
);

// Thunk to cases document
export const updateCase = createAsyncThunk(
  "cases/update",
  async (cases, { rejectWithValue }) => {
    try {
      const response = await updateCaseDetails(cases);
      toast.success(t("DocumentDetailsSavedMessage"));
      return response.data;
    } catch (error) {
      return rejectWithValue(error || error.message || t("Error saving document Details"));
    }
  }
);

export const updateCaseJson = createAsyncThunk(
  "cases/updateJson",
  async (cases, { rejectWithValue }) => {
    try {
      const response = await updateCaseJsonAPI(cases);
      toast.success(t("DocumentDetailsSavedMessage"));
      return response.data;
    } catch (error) {
      return rejectWithValue(error || error.message || t("Error saving document Details"));
    }
  }
);

// Delete Case
export const deleteCases = createAsyncThunk("cases/delete", async (cases, thunkAPI) => {
  try {
    const response = await removeCases(cases);
    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error || "Something went wrong");
  }
});

// Get Case Type
export const fetchAllCaseTypes = createAsyncThunk("cases/fetchCaseTypes", async (cases, thunkAPI) => {
  try {
    const response = await getCaseTypesAPI(cases); 
    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error || "Something went wrong");
  }
});